import { SignUp } from '@/utils/types';
import { jwtDecode } from 'jwt-decode';

// Track ongoing refresh to prevent multiple simultaneous refreshes
let refreshPromise: Promise<boolean> | null = null;

// Check if a token is expired or will expire soon in 5 minutes
function isTokenExpired(token: string | null, bufferSeconds = 300): boolean {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token will expire within the buffer period
    return decoded.exp <= currentTime + bufferSeconds;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
}

// Refresh tokens when needed
export async function refreshToken(): Promise<boolean> {
  // If a refresh is already in progress, return that promise
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    return false;
  }
  
  refreshPromise = new Promise(async (resolve) => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }
      
      const data = await response.json();
      
      if (data.success) {
        if (data.data.AccessToken) {
          localStorage.setItem('accessToken', data.data.AccessToken);
        }
        if (data.data.IdToken) {
          localStorage.setItem('idToken', data.data.IdToken);
        }
        if (data.data.ExpiresIn) {
          localStorage.setItem('expiresIn', data.data.ExpiresIn);
        }
        resolve(true);
      } else {
        throw new Error(data.error || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      
      // Clear tokens on failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('expiresIn');
      // Redirect to login if not already there
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/auth/login')) {
        window.location.href = `/${window.location.pathname.split('/')[1]}/auth/login`;
      }
      
      resolve(false);
    } finally {
      refreshPromise = null;
    }
  });
  
  return refreshPromise;
}

// Get a valid access token, refreshing if necessary
export async function getAccessToken(): Promise<string | null> {
  const accessToken = localStorage.getItem('accessToken');
  
  // If token is expired or will expire soon, refresh it
  if (isTokenExpired(accessToken)) {
    const refreshed = await refreshToken();
    if (!refreshed) {
      return null;
    }
    return localStorage.getItem('accessToken');
  }
  
  return accessToken;
}

// In your fetchWithAuth function in lib/api-client.ts
export async function fetchWithAuth(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  // Get a valid access token
  let accessToken = await getAccessToken();
  
  if (!accessToken) {
    throw new Error('Not authenticated');
  }
  
  // Add the authorization header
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
  
  // Make the request
  let response = await fetch(url, {
    ...options,
    headers,
  });
  
  // If unauthorized, try refreshing the token once
  if (response.status === 401) {
    const refreshed = await refreshToken();
    
    if (refreshed) {
      // Get the new token
      accessToken = await getAccessToken();
      
      if (!accessToken) {
        throw new Error('Authentication failed after token refresh');
      }
      
      // Retry the request with the new token
      response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
    } else {
      // If refresh failed, it likely means the refresh token has expired
      // Redirect to login
      if (typeof window !== 'undefined') {
        const locale = window.location.pathname.split('/')[1] || 'en';
        window.location.href = `/${locale}/auth/login?expired=true`;
      }
    }
  }
  
  return response;
}

export async function signIn(email: string, password: string) {
  try {
    const response = await fetch('/api/auth/signin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    
    const data = await response.json();
        
    if (data.success) {
      // Store tokens in localStorage or a secure cookie
      // Note: For production, consider using more secure storage methods
      if (data.data.AccessToken) {
        localStorage.setItem('accessToken', data.data.AccessToken);
      }
      if (data.data.IdToken) {
        localStorage.setItem('idToken', data.data.IdToken);
      }
      if (data.data.RefreshToken) {
        localStorage.setItem('refreshToken', data.data.RefreshToken);
      }
      if (data.data.ExpiresIn) {
        localStorage.setItem('expiresIn', data.data.ExpiresIn);
      }
    }
    
    return data;
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'An error occurred during sign in'
    };
  }
}

export async function fetchUser() {
  try {
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch('/api/auth/user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accessToken }),
    });
    
    const data = await response.json();
    
    return {
      success: response.ok,
      authenticated: data.authenticated,
      user: data.user,
      message: data.message,
      error: data.error,
    };
  } catch (error: any) {
    console.error('Error checking auth status:', error);
    return {
      success: false,
      authenticated: false,
      message: 'Failed to check authentication status',
      error: error.message
    };
  }
}

export async function signOut() {
  try {
    const accessToken = localStorage.getItem('accessToken');
    
    if (accessToken) {
      // Call the server to invalidate the session
      const response = await fetch('/api/auth/signout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        console.error('Server-side sign out failed:', data.error);
      }
    }
    
    // Always clear local storage, even if the server request fails
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    
    return {
      success: true,
      message: 'Successfully signed out'
    };
  } catch (error: any) {
    console.error('Error during sign out:', error);
    
    // Still clear local storage on error
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    
    return {
      success: true, // Still consider it a success if we cleared local storage
      message: 'Signed out locally, but server session may still be active',
      error: error.message
    };
  }
}

export async function signUp(signup: SignUp) {
  try {
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(signup),
    });
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'An error occurred during sign up'
    };
  }
}

export async function confirmSignUp(email: string, code: string) {
  try {
    const response = await fetch('/api/auth/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'An error occurred during confirmation'
    };
  }
}

export async function forgotPassword(email: string) {
  try {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'An error occurred'
    };
  }
}

export async function resetPassword(email: string, code: string, newPassword: string) {
  try {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code, newPassword }),
    });
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'An error occurred'
    };
  }
}